import type { TFunction } from 'i18next';
import { buildCsv } from 'utils/csv';
import { formatDateTime, formatDay } from 'utils/formatDate';
import type { FollowUpRequest } from 'api/interventionRequestsApi';
import {
  interventionDay,
  knownRefusalReasonLabel,
  pendingSolicitationsLabel,
  technicianName,
} from 'pages/AdminIssues/components/upcomingAssignment';
import { followUpStatusLabel } from './vocabulary';

/**
 * The follow-up export.
 *
 * **Every coded column exports its translated label, never the code** (D25, FLOW-017 §18): a file
 * holding `too_far` is a file whose reader has to know our database. The whole point of the
 * refusal being a code is that it can be counted — and it is counted in a spreadsheet, by someone
 * reading the words.
 *
 * The refusal is split in two columns rather than joined like `refusalDetail` does on screen: a
 * spreadsheet filters on a column, and `Magasin trop éloigné · il est à 200 km` in one cell cannot
 * be grouped. A legacy refusal carries text and no code, so both halves are independently empty.
 */
export const buildFollowUpCsv = (
  t: TFunction,
  requests: FollowUpRequest[],
  withParent = false
): string => {
  const header = [
    t('Key'),
    // Split desks only, like the column on screen.
    ...(withParent ? [t('Parent')] : []),
    t('Store'),
    t('City'),
    t('Type'),
    // Two lists and never one: what the job asks for is not what the man can do, and a
    // spreadsheet that merges them cannot answer either question.
    t('Mission skills'),
    t('Due by'),
    t('Chosen slot'),
    t('Technician'),
    t('Technician skills'),
    // One line per mission since D47: without this the file cannot say that the row stands for
    // five requests, and a reader would count missions as requests.
    t('Solicitations'),
    t('Status'),
    t('Refusal reason'),
    t('Refusal details'),
    t('Sent on'),
    t('Answered on'),
    t('Response time (min)'),
    t('Sent by'),
    t('Outside perimeter'),
  ];

  const rows = requests.map((request) => {
    /*
     * The same rule as the screen (D54) — a spreadsheet outlives it, and would keep attributing
     * the mission. **Empty** rather than the screen's sentence: the Solicitations column already
     * carries the count, and a name column must hold names for a reader to group on it.
     */
    const awaiting = pendingSolicitationsLabel(t, request.status, request.liveRequestCount) !== null;
    return [
      request.issueKey,
      ...(withParent ? [request.parentKey ?? ''] : []),
      // Empty, never `—`: a dash in a spreadsheet is a value someone will filter on by mistake.
      request.store ?? '',
      request.storeCity ?? '',
      request.requestType ?? '',
      request.missionSpecialties.join(' · '),
      interventionDay(request.interventionDate) ?? '',
      request.chosenDate && request.chosenTime
        ? `${formatDay(request.chosenDate)} ${request.chosenTime}`
        : '',
      awaiting ? '' : technicianName(request),
      awaiting ? '' : request.technicianSpecialties.join(' · '),
      String(request.missionRequestCount),
      followUpStatusLabel(t, request.status),
      // An unexpected code off the wire exports nothing rather than a guessed label.
      knownRefusalReasonLabel(t, request.refusalReasonCode) ?? '',
      request.refusalReason ?? '',
      formatDateTime(request.sentAt),
      request.respondedAt ? formatDateTime(request.respondedAt) : '',
      // Minutes, and only once answered: the seconds are server-computed, and a delay on a row
      // still waiting would be the age of the wait under a column that means something else.
      request.responseDelayS == null ? '' : String(Math.round(request.responseDelayS / 60)),
      request.sentByUsername ?? '',
      // Only the bypasses are affirmed. `null` predates the column, and exporting it as "non"
      // would claim a compliance check that never ran (D22).
      request.sentOutsidePerimeter === true ? t('Yes') : '',
    ];
  });

  return buildCsv(header, rows);
};
